import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { 
  Star, 
  Award, 
  Shield, 
  Clock, 
  MessageCircle, 
  X, 
  Quote, 
  MapPin, 
  ChevronLeft, 
  ChevronRight,
  Sparkles,
  Truck,
  Wrench,
  Heart,
  ArrowRight,
  Layout,
  Grid,
  Columns,
  Square,
  Bath,
  Table,
  Coffee,
  Sofa,
  Users,
  Timer,
  BadgeCheck,
  Gem,
  Zap,
  Percent,
  Gift,
  TrendingUp,
  CheckCircle
} from 'lucide-react';
import type { CustomizationType as CustomizationTypeEnum, KitchenLayout } from '../types';

const marketingBadges = {
  'limited-offer': {
    text: 'Limited Time Offer',
    icon: Timer,
    color: 'bg-red-500'
  },
  'best-seller': {
    text: 'Best Seller',
    icon: TrendingUp,
    color: 'bg-gold'
  },
  'new': {
    text: 'New Design',
    icon: Sparkles,
    color: 'bg-blue-500'
  }
};

const customizationTypes = [
  {
    id: 'dining-table',
    name: 'Saloon & Dining Tables',
    description: 'Custom tables for your living room & dining spaces',
    popularFeatures: ['Multiple sizes', 'Premium finishes', 'Custom edges'],
    leadTime: '2-3 weeks',
    startingPrice: 1499,
    rating: 4.9,
    reviews: 847,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    badge: 'new',
    promotion: '15% off on all dining sets',
    savings: '€450',
    satisfaction: '99%',
    warranty: '25 years',
    subtypes: [
      {
        id: 'dining',
        name: 'Dining Table',
        description: 'Elegant dining tables for family gatherings',
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        icon: Table,
        promotion: 'Free chairs with purchase',
        badge: 'best-seller'
      },
      {
        id: 'coffee',
        name: 'Coffee Table',
        description: 'Stylish centerpieces for your living room',
        image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
        icon: Coffee,
        promotion: '20% off this week',
        badge: 'limited-offer'
      },
      {
        id: 'console',
        name: 'Console Table',
        description: 'Elegant entryway and hallway tables',
        image: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
        icon: Sofa,
        promotion: 'Free delivery & installation',
        badge: 'new'
      }
    ]
  },
  {
    id: 'countertop',
    name: 'Kitchen Surfaces',
    description: 'Premium kitchen countertops & islands',
    popularFeatures: ['Seamless design', 'Built-in features', 'Heat resistant'],
    leadTime: '1-2 weeks',
    startingPrice: 999,
    rating: 4.8,
    reviews: 1243,
    badge: 'best-seller',
    image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
    promotion: 'Free sink with any purchase',
    savings: '€300',
    satisfaction: '98%',
    warranty: 'Lifetime',
    subtypes: [
      {
        id: 'L-shaped',
        name: 'L-Shaped Kitchen',
        description: 'Perfect for corner spaces and open layouts',
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        icon: Layout,
        promotion: 'Free 3D design service',
        badge: 'best-seller'
      },
      {
        id: 'U-shaped',
        name: 'U-Shaped Kitchen',
        description: 'Maximum storage and workspace',
        image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
        icon: Grid,
        promotion: 'Premium appliances package',
        badge: 'limited-offer'
      },
      {
        id: 'straight',
        name: 'Straight Kitchen',
        description: 'Efficient single-wall design',
        image: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
        icon: Square,
        promotion: 'Save 25% this month',
        badge: 'new'
      },
      {
        id: 'island',
        name: 'Kitchen Island',
        description: 'Standalone workspace and gathering point',
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        icon: Columns,
        promotion: 'Free bar stools included',
        badge: 'limited-offer'
      }
    ]
  },
  {
    id: 'vanity',
    name: 'Bathroom Collection',
    description: 'Luxury bathroom surfaces, washbasins & vanities',
    popularFeatures: ['Integrated sinks', 'Anti-bacterial', 'Moisture resistant'],
    leadTime: '1-2 weeks',
    startingPrice: 799,
    rating: 4.9,
    reviews: 562,
    badge: 'limited-offer',
    image: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
    promotion: 'Complete bathroom bundle',
    savings: '€200',
    satisfaction: '97%',
    warranty: '15 years',
    subtypes: [
      {
        id: 'wall-mounted',
        name: 'Wall-Mounted Vanity',
        description: 'Modern floating design',
        image: 'https://images.unsplash.com/photo-1600607687668-0b7b08d9d1a5?w=800',
        icon: Bath,
        promotion: 'Free LED mirror',
        badge: 'best-seller'
      },
      {
        id: 'freestanding',
        name: 'Freestanding Vanity',
        description: 'Traditional floor-mounted style',
        image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
        icon: Bath,
        promotion: 'Save €150 today',
        badge: 'limited-offer'
      },
      {
        id: 'corner',
        name: 'Corner Vanity',
        description: 'Space-saving corner solution',
        image: 'https://images.unsplash.com/photo-1600607687644-c7171b42498b?w=800',
        icon: Bath,
        promotion: 'Free storage solutions',
        badge: 'new'
      }
    ]
  },
  {
    id: 'other',
    name: 'Custom Project',
    description: 'Bespoke designs for spaces. Email us to bring your vision to life',
    popularFeatures: ['Custom designs', 'Expert guidance', 'Bespoke solutions'],
    leadTime: '2-4 weeks',
    startingPrice: 599,
    rating: 4.8,
    reviews: 324,
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800',
    promotion: 'Free consultation',
    savings: 'Custom',
    satisfaction: '100%',
    warranty: 'Custom'
  }
];

const pageTransitionVariants = {
  initial: {
    opacity: 0,
    scale: 0.98
  },
  animate: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1
    }
  },
  exit: {
    opacity: 0,
    scale: 1.02,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const fadeUpVariants = {
  initial: { 
    opacity: 0, 
    y: 40,
    scale: 0.9
  },
  animate: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    y: -40,
    transition: {
      duration: 0.4,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const staggerContainerVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.3
    }
  },
  exit: { opacity: 0 }
};

const cardEntryVariants = {
  initial: { 
    opacity: 0,
    y: 40,
    scale: 0.9
  },
  animate: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const glowVariants = {
  initial: {
    opacity: 0,
    scale: 0.95
  },
  hover: {
    opacity: [0.4, 0.2, 0.4],
    scale: 1.05,
    transition: {
      duration: 1.5,
      repeat: Infinity,
      repeatType: "reverse",
      ease: "linear"
    }
  }
};

export const CustomizationType: React.FC = () => {
  const { setCustomizationOptions, setStep } = useCustomizationStore();
  const { currency } = useLocalizationStore();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState({ hours: 48, minutes: 0 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59 };
        }
        return prev;
      });
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const handleTypeSelect = (typeId: string) => {
    const type = customizationTypes.find(t => t.id === typeId);
    if (type?.subtypes) {
      setSelectedType(typeId);
    } else if (typeId === 'other') {
      setCustomizationOptions({
        type: 'custom-project',
        width: 1000,
        length: 2000,
        edgeProfile: 'straight',
        thickness: 12
      });
      setStep('inquiry');
    }
  };

  const handleSubtypeSelect = (typeId: string, subtypeId: string) => {
    const defaultDimensions = {
      'dining-table': { width: 1000, length: 2000 },
      'countertop': { width: 600, length: 3000 },
      'vanity': { width: 600, length: 1200 }
    }[typeId as CustomizationTypeEnum];

    setCustomizationOptions({
      type: typeId as CustomizationTypeEnum,
      subtype: subtypeId,
      thickness: 12,
      width: defaultDimensions.width,
      length: defaultDimensions.length,
      edgeProfile: 'straight',
      kitchenLayout: subtypeId as KitchenLayout
    });
  };

  const renderBadge = (badgeType: string) => {
    const badge = marketingBadges[badgeType];
    if (!badge) return null;

    const BadgeIcon = badge.icon;
    return (
      <div className={`absolute top-4 right-4 ${badge.color} text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg text-xs sm:text-sm`}>
        <BadgeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
        <span className="font-medium">{badge.text}</span>
      </div>
    );
  };

  const selectedTypeData = selectedType ? customizationTypes.find(t => t.id === selectedType) : null;

  return (
    <motion.div
      variants={pageTransitionVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen bg-cream py-8 sm:py-16 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!selectedType ? (
          <>
            <motion.div
              variants={fadeUpVariants}
              className="bg-gradient-to-r from-gold to-gold-dark text-white p-4 sm:p-6 rounded-xl mb-6 sm:mb-8 text-center"
            >
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
                <Timer className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="font-semibold text-sm sm:text-base">
                  Special Offer Ends In: {timeLeft.hours}h {timeLeft.minutes}m
                </span>
                <Gift className="w-5 h-5 sm:w-6 sm:h-6" />
              </div>
              <p className="text-xs sm:text-sm mt-2">
                🎉 Up to 25% off on selected items + Free Installation
              </p>
            </motion.div>

            <motion.div
              variants={fadeUpVariants}
              className="flex flex-col items-center mb-8 sm:mb-16"
            >
              <div className="text-center max-w-3xl">
                <div className="inline-flex items-center gap-2 bg-gold/10 text-gold px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6">
                  <Award className="w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="font-medium text-sm sm:text-base">Europe's Most Trusted Surface Provider</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gold-dark mb-4 sm:mb-6">
                  Premium Porcelain Surfaces
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-black/70 mb-6 sm:mb-8">
                  Transform your space with our exclusive collection of European porcelain. 
                  Unmatched quality, timeless elegance.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-2" />
                    <p className="font-bold text-gold-dark text-lg sm:text-2xl">25,000+</p>
                    <p className="text-xs sm:text-sm text-black/70">Happy Customers</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md">
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-2" />
                    <p className="font-bold text-gold-dark text-lg sm:text-2xl">25 Years</p>
                    <p className="text-xs sm:text-sm text-black/70">Warranty</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md">
                    <BadgeCheck className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-2" />
                    <p className="font-bold text-gold-dark text-lg sm:text-2xl">98.5%</p>
                    <p className="text-xs sm:text-sm text-black/70">Satisfaction Rate</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md">
                    <Gem className="w-5 h-5 sm:w-6 sm:h-6 text-gold mx-auto mb-2" />
                    <p className="font-bold text-gold-dark text-lg sm:text-2xl">Premium</p>
                    <p className="text-xs sm:text-sm text-black/70">Quality Grade</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              variants={staggerContainerVariants}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8"
            >
              {customizationTypes.map((type, index) => {
                const Icon = type.icon;
                return (
                  <motion.div
                    key={type.id}
                    variants={cardEntryVariants}
                    custom={index}
                    layoutId={`type-${type.id}`}
                    onClick={() => handleTypeSelect(type.id)}
                    className="relative bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer group"
                  >
                    <div className="relative h-48 sm:h-64">
                      <img
                        src={type.image}
                        alt={type.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      {type.badge && renderBadge(type.badge)}
                      <div className="absolute bottom-4 left-4 text-white">
                        <h3 className="text-lg sm:text-xl font-semibold mb-1 sm:mb-2">{type.name}</h3>
                        <p className="text-sm text-white/80">{type.description}</p>
                      </div>
                    </div>

                    <div className="p-4 sm:p-6">
                      <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-gold fill-current" />
                          <span className="text-sm font-medium">{type.rating}</span>
                        </div>
                        <div className="text-xs sm:text-sm text-black/60">
                          {type.reviews} verified reviews
                        </div>
                      </div>

                      <div className="bg-gold/10 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-gold">
                          <Percent className="w-4 h-4" />
                          <p className="text-sm font-medium">{type.promotion}</p>
                        </div>
                        {type.savings !== 'Custom' && (
                          <p className="text-xs text-black/70 mt-1">
                            Save up to {type.savings}
                          </p>
                        )}
                      </div>

                      <ul className="space-y-2 mb-4">
                        {type.popularFeatures.map((feature) => (
                          <li key={feature} className="text-sm text-black/70 flex items-center">
                            <CheckCircle className="w-4 h-4 text-gold mr-2 flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      <div className="flex items-center gap-4 mb-4 text-xs text-black/60">
                        <div className="flex items-center gap-1">
                          <Shield className="w-4 h-4 text-gold" />
                          {type.warranty} warranty
                        </div>
                        <div className="flex items-center gap-1">
                          <Heart className="w-4 h-4 text-gold" />
                          {type.satisfaction} satisfied
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="text-gold-dark">
                          <span className="text-xs sm:text-sm">Starting from</span>
                          <div className="text-lg sm:text-xl font-bold">
                            {formatCurrency(type.startingPrice, currency)}
                          </div>
                          <span className="text-xs text-black/60">Includes installation</span>
                        </div>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="bg-gold text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gold-dark transition-colors"
                        >
                          <span className="text-sm sm:text-base">Customize</span>
                          <ArrowRight className="w-4 h-4" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              variants={fadeUpVariants}
              className="mt-8 sm:mt-16 text-center"
            >
              <h3 className="text-xl sm:text-2xl font-bold text-gold-dark mb-6 sm:mb-8">Why Choose Our Premium Surfaces</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                  <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
                  <h4 className="font-semibold text-gold-dark mb-2">Quality Guaranteed</h4>
                  <p className="text-xs sm:text-sm text-black/70">Premium European materials with extended warranty</p>
                </div>
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                  <Truck className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
                  <h4 className="font-semibold text-gold-dark mb-2">Professional Delivery</h4>
                  <p className="text-xs sm:text-sm text-black/70">White-glove delivery service included</p>
                </div>
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                  <Wrench className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
                  <h4 className="font-semibold text-gold-dark mb-2">Expert Installation</h4>
                  <p className="text-xs sm:text-sm text-black/70">Certified installation team</p>
                </div>
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md">
                  <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-gold mx-auto mb-3 sm:mb-4" />
                  <h4 className="font-semibold text-gold-dark mb-2">Lifetime Support</h4>
                  <p className="text-xs sm:text-sm text-black/70">Dedicated customer service</p>
                </div>
              </div>
            </motion.div>
          </>
        ) : (
          <motion.div
            variants={fadeUpVariants}
            className="max-w-4xl mx-auto"
          >
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => setSelectedType(null)}
              className="flex items-center text-gold hover:text-gold-dark transition-colors mb-6 sm:mb-8"
            >
              <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Back to Categories
            </motion.button>

            <motion.div
              variants={fadeUpVariants}
              className="text-center mb-8 sm:mb-12"
            >
              <h2 className="text-2xl sm:text-4xl font-bold text-gold-dark mb-3 sm:mb-4">
                {selectedTypeData?.name}
              </h2>
              <p className="text-base sm:text-xl text-black/70 mb-4 sm:mb-6">
                Select the perfect style for your space
              </p>
              <div className="inline-flex items-center gap-2 bg-gold/10 text-gold px-3 sm:px-4 py-1.5 sm:py-2 rounded-full">
                <Timer className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm font-medium">
                  Limited time offer ends in {timeLeft.hours}h {timeLeft.minutes}m
                </span>
              </div>
            </motion.div>

            <motion.div
              variants={staggerContainerVariants}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8"
            >
              {selectedTypeData?.subtypes?.map((subtype, index) => {
                const Icon = subtype.icon;
                return (
                  <motion.div
                    key={subtype.id}
                    variants={cardEntryVariants}
                    custom={index}
                    layoutId={`subtype-${subtype.id}`}
                    onClick={() => handleSubtypeSelect(selectedType, subtype.id)}
                    className="bg-white rounded-xl shadow-lg overflow-hidden cursor-pointer group"
                  >
                    <div className="relative h-40 sm:h-48">
                      <img
                        src={subtype.image}
                        alt={subtype.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      {subtype.badge && renderBadge(subtype.badge)}
                    </div>
                    <div className="p-4 sm:p-6">
                      <div className="flex items-center gap-3 sm:gap-4 mb-4">
                        <div className="p-2 sm:p-3 bg-gold/10 rounded-lg">
                          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-gold" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gold-dark text-base sm:text-lg">
                            {subtype.name}
                          </h3>
                          <p className="text-xs sm:text-sm text-black/70">
                            {subtype.description}
                          </p>
                        </div>
                      </div>

                      <div className="bg-gold/10 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 text-gold">
                          <Gift className="w-4 h-4" />
                          <p className="text-xs sm:text-sm font-medium">{subtype.promotion}</p>
                        </div>
                      </div>

                      <motion.div
                        className="flex items-center justify-end gap-2 text-gold"
                        variants={glowVariants}
                        initial="initial"
                        whileHover="hover"
                      >
                        <span className="text-sm font-medium">Customize Now</span>
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                      </motion.div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            <motion.div
              variants={fadeUpVariants}
              className="mt-8 sm:mt-12 bg-gradient-to-r from-gold to-gold-dark rounded-xl p-6 sm:p-8 text-white text-center"
            >
              <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-3 sm:mb-4" />
              <h3 className="text-xl sm:text-2xl font-bold mb-2">Need Expert Guidance?</h3>
              <p className="text-sm sm:text-base mb-4 sm:mb-6">Our design consultants are here to help you make the perfect choice</p>
              <button className="bg-white text-gold px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-cream transition-colors inline-flex items-center gap-2">
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                Schedule Free Consultation
              </button>
            </motion.div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};